import React from 'react'
import styled from 'styled-components'
import CrossIcon from '../assets/cross.svg'
import { desktopVw } from '../styles/utils'

import { Container, Input, RadioGroup, RadioSelection, RatingInput, Select, TextArea } from './FormElements'

import Button from './Button'
import ModalHeader from './ModalHeader'
import { useTranslation } from 'next-i18next'

const StyledForm = styled.form`
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    min-width: ${desktopVw(603)};
  }
`

const FieldsContainer = styled.div`
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin: 0 -${desktopVw(10)};
  }
`

const StyledContainer = styled(Container)`
  width: 100%;
  &.container--radio-selection {
    + div:not(.container--radio-selection) {
      margin-top: 32px;
    }
  }

  .form_input_container {
    width: 100%;
  }

  &.form_select_container {
    flex-direction: column;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding: 0 ${desktopVw(10)};
    margin-bottom: ${desktopVw(15)};
    &.container__half {
      display: inline-flex;
      flex-direction: column;
      width: 50%;
      box-sizing: border-box;
    }

    &.container--radio-selection {
      + div:not(.container--radio-selection) {
        margin-top: ${desktopVw(36)};
      }
    }
  }
`

const SubmitButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${props => props.theme.fonts.grosa};
  border-radius: 100px;
  text-transform: uppercase;
  font-weight: 500;
  width: 100%;
  margin-top: 31px;
  span {
    font-size: 12px;
    letter-spacing: 0.6px;
  }

  &:disabled {
    background: ${props => props.theme.colors.greyLighter};
    cursor: initial;
    border-color: ${props => props.theme.colors.greyLighter};
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin: ${desktopVw(32)} 0 0;
    padding: ${desktopVw(16)} ${desktopVw(20)};
    height: ${desktopVw(48)};
    width: ${desktopVw(195)};
  }
`

const CheckBox = styled.div`
  height: ${desktopVw(15)};
  width: ${desktopVw(15)};
  margin: 0 ${desktopVw(12)} 0 0;
  border: solid ${desktopVw(1)} black;
  cursor: pointer;
  position: relative;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px {
    height: ${desktopVw(13)};
    width: ${desktopVw(13)};
  }
`
const StyledCrossIcon = styled(CrossIcon)`
  height: ${desktopVw(15)};
  width: ${desktopVw(15)};
  position: absolute;
  left: ${desktopVw(-1)};
  top: ${desktopVw(-1)};

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px {
    height: ${desktopVw(13)};
    width: ${desktopVw(13)};
  }
`

const DefaultAddressContainer = styled.div`
  margin-left: 0px;
  display: flex;
  font-family: ${props => props.theme.fonts.grosa};
  font-size: 13px;
  line-height: 19px;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-left: ${desktopVw(10)};
    font-size: ${desktopVw(13)};
    line-height: ${desktopVw(19)};
  }
`

const Form = ({
  children,
  fields,
  title,
  register,
  errors,
  formValues,
  handleSubmit,
  disableSubmit,
  submitButtonText,
  loading,
  defaultAddressCheckbox = false,
  onCheckboxChange,
  defaultAddress,
}) => {
  const { t } = useTranslation('accountPage')
  return (
    <StyledForm onSubmit={handleSubmit}>
      <ModalHeader title={title} />
      <FieldsContainer>
        {fields.map(f => {
          switch (f.__typename) {
            case 'ContentfulInput':
              return (
                <React.Fragment key={f.id}>
                  {f.type === 'text' && (
                    <StyledContainer className={!f.width && 'container__half'}>
                      <Input
                        placeholder={f.placeholder}
                        aria-label={f.label}
                        name={f.name}
                        defaultValue={formValues?.[f.name]}
                        type={f.type}
                        register={register}
                        errors={errors}
                        required={f.required === false ? false : true}
                        containerClassName="form_input_container"
                        pattern={f.pattern}
                        patternMessage={f.patternMessage}
                        maxLength={f.maxLength}
                      />
                    </StyledContainer>
                  )}
                  {f.type === 'textarea' && (
                    <StyledContainer className={!f.width && 'container__half'}>
                      <TextArea
                        name={f.name}
                        placeholder={f.placeholder}
                        defaultValue={formValues?.[f.name]}
                        register={register}
                        errors={errors}
                        required={f.required}
                        maxLength={f.maxLength}
                      />
                    </StyledContainer>
                  )}
                </React.Fragment>
              )

            case 'ContentfulRadio':
              return (
                <React.Fragment key={f.id}>
                  {f.type === 'rating' && (
                    <StyledContainer>
                      <RatingInput name={f.name} range={f.range} register={register} errors={errors} />
                    </StyledContainer>
                  )}
                  {f.type === 'range' && (
                    <StyledContainer className="container--radio-selection">
                      <RadioSelection
                        label={f.label}
                        name={f.name}
                        range={f.range}
                        register={register}
                        errors={errors}
                        displayValues={f.displayValues}
                      />
                    </StyledContainer>
                  )}
                  {f.type === 'radio' && (
                    <StyledContainer>
                      <RadioGroup
                        label={f.label}
                        name={f.name}
                        options={f.radioOptions}
                        register={register}
                        errors={errors}
                      />
                    </StyledContainer>
                  )}
                </React.Fragment>
              )

            case 'ContentfulSelect':
              return (
                <StyledContainer
                  key={f.id}
                  className={!f.width ? 'container__half form_select_container' : 'form_select_container'}
                >
                  <Select
                    label={f.label}
                    name={f.name}
                    options={f.options}
                    register={register}
                    defaultValue={formValues?.[f.name]}
                    errors={errors}
                  />
                </StyledContainer>
              )

            default:
              return null
          }
        })}
        {children && <StyledContainer>{children}</StyledContainer>}
        {defaultAddressCheckbox && ( //TODO: This is not generic enough.
          <DefaultAddressContainer>
            <CheckBox onClick={onCheckboxChange}>{defaultAddress && <StyledCrossIcon />}</CheckBox>
            <span>{t('setAsDefault')}</span>
          </DefaultAddressContainer>
        )}
        <SubmitButton
          black
          // @i18n-ignore
          type="submit"
          loading={loading}
          disabled={disableSubmit}
        >
          {submitButtonText}
        </SubmitButton>
      </FieldsContainer>
    </StyledForm>
  )
}

export default Form
